import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  map,
  Observable,
  Subscription,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { Store } from '@ngrx/store';

import {
  ButtonTheme,
  IconButtonSize,
  IconButtonTheme,
  IconPosition,
  LoaderTheme,
  ModalButtons,
  NotificationTheme,
} from '@fcom/ui-components';
import { HeaderItem, RowDataItem } from '@fcom/ui-components/components/table/table.component';
import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';
import { TzDate, unsubscribe } from '@fcom/core/utils';
import { ScrollService, ToasterService } from '@fcom/common';
import { AppState, ConfigService, WindowRef } from '@fcom/core';
import { profile } from '@fcom/core/selectors';
import { CorporateUserRole, Profile } from '@fcom/core-api/login';
import { splitPhoneNumber } from '@fcom/common-booking/utils/common-booking.utils';
import { CorporateService } from '@fcom/common/services/corporate/corporate.service';
import {
  CorporateSalesForceUser,
  CorporateUser,
  CorporateUserForm,
  PassportDetailsForm,
  PersonalDetailsForm,
  UserDetailsForm,
} from '@fcom/common/interfaces';
import { isCorporateAdminOrTravelBooker } from '@fcom/common/login';
import { LinkSize } from '@fcom/ui-components/components/link/enums';

import { parsePhoneNumber } from '../../utils/utils';

interface TableLink {
  url: string;
  title: string;
}

interface DataInterface {
  label?: string;
  value?: string;
  link?: TableLink;
  allowEmpty?: boolean;
  type?: DataType;
}

enum DataType {
  TEXT,
  DATE,
}

export enum UserFormType {
  PERSONAL_DETAILS,
  PASSPORT_DETAILS,
  USER_DETAILS,
}

@Component({
  selector: 'fin-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  protected readonly LoaderTheme = LoaderTheme;
  protected readonly IconPosition = IconPosition;
  protected readonly ButtonTheme = ButtonTheme;
  protected readonly DataType = DataType;
  protected readonly NotificationTheme = NotificationTheme;
  protected readonly ModalButtons = ModalButtons;
  protected readonly UserFormType = UserFormType;
  protected readonly IconButtonTheme = IconButtonTheme;
  protected readonly IconButtonSize = IconButtonSize;
  protected readonly SvgLibraryIcon = SvgLibraryIcon;
  protected readonly LinkSize = LinkSize;

  @Input()
  userId$: Observable<string>;

  @Output()
  navigatedBack = new EventEmitter<void>();

  profile$: Observable<Profile>;
  user$: BehaviorSubject<CorporateUser> = new BehaviorSubject(null);
  formGroup: FormGroup<CorporateUserForm>;
  personalDetailsForm: FormGroup<PersonalDetailsForm>;
  passportDetailsForm: FormGroup<PassportDetailsForm>;
  userDetailsForm: FormGroup<UserDetailsForm>;

  subscriptions = new Subscription();

  headers: HeaderItem[] = [{ key: 'label' }, { key: 'value' }];
  userDetailsHeaders: HeaderItem[] = [{ key: 'label' }, { key: 'value' }, { key: 'link' }];
  personalDetailsData$: Observable<RowDataItem<DataInterface>[]>;
  passportDetailsData$: Observable<RowDataItem<DataInterface>[]>;
  userDetailsData$: Observable<RowDataItem<DataInterface>[]>;

  passportNotification$: Observable<string | undefined>;
  isAdminOrTravelBooker$: Observable<boolean>;
  deactivationEnabled$: Observable<boolean>;

  updating = false;
  confirmModalOpen = false;
  editModalOpen = false;
  aboutModalOpen = false;
  editModalTitle: string;
  editFormType: UserFormType;

  constructor(
    private cd: ChangeDetectorRef,
    private store$: Store<AppState>,
    private corporateService: CorporateService,
    private languageService: LanguageService,
    private toasterService: ToasterService,
    private scrollService: ScrollService,
    private configService: ConfigService,
    private windowRef: WindowRef,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private location: Location
  ) {}
  ngOnInit(): void {
    this.profile$ = this.store$.pipe(profile(), finShare());
    this.isAdminOrTravelBooker$ = this.profile$.pipe(
      map((p) => isCorporateAdminOrTravelBooker(p)),
      finShare()
    );
    this.refreshUser();
    this.scrollService.scrollTop();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  navigateBack(): void {
    this.replaceUrl();
    this.navigatedBack.emit();
  }

  editPersonalDetails(): void {
    this.openEditModal('CORPORATE.corporatePortal.personalDetails', UserFormType.PERSONAL_DETAILS);
  }

  editPassportDetails(): void {
    this.openEditModal('CORPORATE.corporatePortal.passportDetails', UserFormType.PASSPORT_DETAILS);
  }

  editUserDetails(): void {
    this.openEditModal('CORPORATE.corporatePortal.userDetails', UserFormType.USER_DETAILS);
  }

  updateUser(): void {
    this.updating = true;
    const payload = this.getPayload();
    const curr = this.user$.value;
    this.user$.next({
      ...curr,
      ...this.payloadToCorporateUser(payload),
    });
    this.subscriptions.add(
      this.user$
        .pipe(
          switchMap((user) => this.corporateService.updateUser(user.userId, payload)),
          catchError((err: unknown) => {
            this.updating = false;
            this.toasterService.addMessageToQueue({
              id: 'corporate-portal-error',
              contentKey: 'CORPORATE.corporatePortal.notifications.commonError',
              theme: NotificationTheme.ALERT,
              autoClose: true,
            });
            this.refreshUser();
            throw err;
          }),
          take(1)
        )
        .subscribe(() => {
          this.toasterService.addMessageToQueue({
            id: 'corporate-portal-success',
            contentKey: 'CORPORATE.corporatePortal.notifications.userUpdated',
            keepMessageAfterNavigation: true,
            autoClose: true,
          });
          this.loadUserData();
        })
    );
  }

  deactivateUser(): void {
    this.confirmModalOpen = true;
  }

  confirmDeactivation(): void {
    this.updating = true;
    this.subscriptions.add(
      this.user$
        .pipe(
          switchMap((user) => this.corporateService.deactivateUser({ id: user.userId, lastname: user.lastname })),
          take(1),
          catchError((err: unknown) => {
            this.confirmModalOpen = false;
            this.updating = false;
            this.toasterService.addMessageToQueue({
              id: 'corporate-portal-error',
              contentKey: 'CORPORATE.corporatePortal.notifications.commonError',
              theme: NotificationTheme.ALERT,
              autoClose: true,
            });
            this.cd.markForCheck();
            throw err;
          })
        )
        .subscribe(() => {
          this.updating = false;
          this.toasterService.addMessageToQueue({
            id: 'corporate-portal-success',
            contentKey: 'CORPORATE.corporatePortal.notifications.userDeactivated',
            keepMessageAfterNavigation: true,
            autoClose: true,
          });
          this.navigateBack();
        })
    );
  }

  private replaceUrl(): void {
    this.subscriptions.add(
      this.activatedRoute.url.pipe(take(1)).subscribe((url) => {
        const lastPathPart = url.slice(-1)?.[0]?.path;
        if (lastPathPart === 'me') {
          const parentUrl = this.router.createUrlTree(['../'], { relativeTo: this.activatedRoute }).toString();
          this.location.go(parentUrl);
        }
      })
    );
  }

  private refreshUser(): void {
    this.subscriptions.add(
      this.userId$.pipe(switchMap((id) => this.corporateService.getUser(id))).subscribe((u) => {
        this.user$.next(u);
        this.loadUserData();
      })
    );
  }

  private loadUserData(): void {
    this.createForms();
    this.personalDetailsData$ = this.user$.pipe(map((u) => this.mapPersonalDetails(u)));
    this.passportDetailsData$ = this.user$.pipe(switchMap((u) => this.mapPassportDetails(u)));
    this.userDetailsData$ = this.user$.pipe(switchMap((u) => this.mapUserDetails(u)));
    this.passportNotification$ = this.user$.pipe(
      map((u) => {
        if (!u.expires) {
          return undefined;
        }
        const d = TzDate.of(u.expires);
        if (d.isBefore(TzDate.now())) {
          return 'CORPORATE.corporatePortal.errors.passportExpired';
        }
      })
    );
    this.deactivationEnabled$ = this.profile$.pipe(
      withLatestFrom(this.user$),
      map(
        ([userProfile, selectedUser]: [Profile, CorporateUser]) =>
          userProfile.corporate.userRole === CorporateUserRole.ADMIN &&
          selectedUser.userId !== userProfile.corporate.userId
      )
    );
    this.updating = false;
    this.cd.markForCheck();
  }

  private getPayload(): CorporateSalesForceUser {
    return {
      title: this.personalDetailsForm.get('salutation').value,
      firstname: this.personalDetailsForm.get('firstName').value,
      lastname: this.personalDetailsForm.get('lastName').value,
      upEmail: this.personalDetailsForm.get('email').value,
      mobileNumber: parsePhoneNumber(
        this.personalDetailsForm.get('phonePrefix').value,
        this.personalDetailsForm.get('phoneNumber').value
      ),
      birthdate: this.personalDetailsForm.get('dateOfBirth').value?.toString(),
      jobTitle: this.personalDetailsForm.get('workTitle').value,
      fplusNumber: this.personalDetailsForm.get('fPlusNumber').value,

      passportNumber: this.passportDetailsForm.get('passportNumber').value,
      issued: this.passportDetailsForm.get('issueDate').value?.toString(),
      expires: this.passportDetailsForm.get('expirationDate').value?.toString(),
      nationality: this.passportDetailsForm.get('nationality').value,

      role: this.userDetailsForm.get('role').value,
    };
  }

  private payloadToCorporateUser(payload: CorporateSalesForceUser): Partial<CorporateUser> {
    return {
      ...payload,
      email: payload.upEmail,
      mobile: payload.mobileNumber,
      finnairPlus: payload.fplusNumber,
    };
  }

  private createForms(): void {
    this.subscriptions.add(
      combineLatest([this.languageService.translate('countryCodes'), this.user$])
        .pipe(take(1))
        .subscribe(([cc, user]) => {
          const phoneNumber = splitPhoneNumber(user?.mobile || user?.workPhone, cc);
          this.formGroup = this.corporateService.getCorporateUserForm(user, phoneNumber);
          this.personalDetailsForm = this.formGroup.get('personalDetails') as FormGroup<PersonalDetailsForm>;
          this.passportDetailsForm = this.formGroup.get('passportDetails') as FormGroup<PassportDetailsForm>;
          this.userDetailsForm = this.formGroup.get('userDetails') as FormGroup<UserDetailsForm>;
        })
    );
  }

  private openEditModal(title: string, formType: UserFormType): void {
    this.editModalTitle = title;
    this.editFormType = formType;
    this.editModalOpen = true;
  }

  private mapPersonalDetails(user: CorporateUser): RowDataItem<DataInterface>[] {
    return this.mapToRowData([
      { label: 'name', value: `${user.firstname} ${user.lastname}` },
      { label: 'email', value: user.email },
      { label: 'phone', value: user.mobile || user.workPhone },
      { label: 'jobTitle', value: user.jobTitle },
      { label: 'dateOfBirth', value: user.birthdate, type: DataType.DATE },
      { label: 'fPlus', value: user.finnairPlus },
    ]);
  }

  private mapPassportDetails(user: CorporateUser): Observable<RowDataItem<DataInterface>[]> {
    return this.languageService.translate('countryCodes').pipe(
      map((countryCodes) => {
        const countryCode = countryCodes.find((cc) => cc.countryCode === user.nationality);
        return this.mapToRowData([
          { label: 'passportNumber', value: user.passportNumber },
          { label: 'releaseDate', value: user.issued, type: DataType.DATE },
          { label: 'expirationDate', value: user.expires, type: DataType.DATE },
          { label: 'nationality', value: countryCode?.nationality || countryCode?.name },
        ]);
      })
    );
  }

  private mapUserDetails(user: CorporateUser): Observable<RowDataItem<DataInterface>[]> {
    return this.profile$.pipe(
      withLatestFrom(
        this.languageService.translateMultiple([
          `CORPORATE.corporatePortal.roles.${user.role}`,
          'CORPORATE.corporatePortal.changePassword',
        ])
      ),
      map(([userProfile, translations]) => {
        const isMe = user.userId === userProfile.corporate.userId;

        return this.mapToRowData([
          {
            label: 'username',
            value: user.userId,
            link: this.getChangePasswordLink(isMe, translations[1]),
            allowEmpty: true,
          },
          { label: 'role', value: translations[0], allowEmpty: true },
        ]);
      })
    );
  }

  private getChangePasswordLink(isMe: boolean, title: string) {
    if (!isMe) {
      return undefined;
    }
    return {
      url: `https://${this.configService.cfg.casHost}/content/${this.languageService.localeValue}/change-password/?returnUrl=${this.windowRef.nativeWindow.location.href}`,
      title,
    };
  }

  private mapToRowData(data: DataInterface[]): RowDataItem<DataInterface>[] {
    return data.map((row) => ({
      label: { data: { label: row.label } },
      value: { data: { value: row.value, type: row.type || DataType.TEXT }, options: { class: 'bold-type' } },
      link: { data: { link: row.link, allowEmpty: row.allowEmpty } },
    }));
  }
}
